import { normalizeCountryCode } from "@src/packages/location/countryPreference";

/**
 * The number a patient dials when the coach tells her to get emergency help.
 *
 * The coach used to answer an `emergency` escalation with a button that opened
 * her care-team screen. That is the wrong destination twice over: it routes an
 * emergency through us, and it hands someone with difficulty breathing a
 * contact list instead of a number. So the coach names the number and gets out
 * of the way — she dials, we are not in the call.
 *
 * Three rules this file follows, all of them for the same reason:
 *
 * - **Never invent a number.** An unlisted country returns `null`, and the UI
 *   says "call your local emergency services" rather than guessing. A wrong
 *   emergency number is worse than no number, because it is dialled with
 *   confidence.
 * - **Name the country.** `resolveEmergencyNumbers` answers for a country code,
 *   and the surface renders that country's name beside the digits. The codes
 *   come from where she seems to be (the IP address on web, the device's
 *   region on a phone) and from her profile — neither is certain to be where
 *   she *is*. Naming the country is what makes a stale answer safe: she reads
 *   "Emergency services in France: 15" and knows immediately whether it applies.
 * - **Say which service answers.** 15 reaches the SAMU and 112 reaches a general
 *   dispatcher; they are not interchangeable, and listing them as a bare pair
 *   would imply they are. The medical number comes first where one exists.
 *
 * The table is deliberately short. It covers the markets Louise operates in and
 * the ones a patient in those markets travels to, and stops where confidence
 * stops. Extending it is a market decision that needs a human check per country
 * before launch, not a guess from here.
 */

export type EmergencyNumber = {
  /** The digits, exactly as dialled inside the country. */
  number: string;
  /**
   * Who answers. `medical` reaches an ambulance dispatcher directly; `general`
   * reaches a common dispatcher that routes on. Rendered as a label, so the two
   * never read as alternatives to each other.
   */
  service: "medical" | "general";
};

export type EmergencyContacts = {
  /** ISO 3166-1 alpha-2, normalised. Rendered so she can check it applies. */
  countryCode: string;
  /** Medical-specific first where the country has one. Never empty. */
  numbers: readonly EmergencyNumber[];
};

/** The single European emergency number, valid across the EU and EEA. */
const EUROPEAN_GENERAL: readonly EmergencyNumber[] = [
  { number: "112", service: "general" },
];

/**
 * EU and EEA states that publish 112 as the number to call, with no separate
 * medical line a patient would be expected to know.
 *
 * France, Austria and Switzerland are absent on purpose — each has a medical
 * dispatcher a patient there is taught to call first, so they are listed
 * individually below.
 */
const EUROPEAN_GENERAL_ONLY = [
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "DE",
  "GR",
  "HU",
  "IS",
  "IT",
  "LV",
  "LI",
  "LT",
  "LU",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
] as const;

const EMERGENCY_NUMBERS: Readonly<Record<string, readonly EmergencyNumber[]>> =
  {
    ...Object.fromEntries(
      EUROPEAN_GENERAL_ONLY.map((code) => [code, EUROPEAN_GENERAL]),
    ),

    // North America. One number, all services.
    US: [{ number: "911", service: "general" }],
    CA: [{ number: "911", service: "general" }],
    MX: [{ number: "911", service: "general" }],

    // Medical dispatch is its own number, and is the one to call first.
    FR: [
      { number: "15", service: "medical" },
      { number: "112", service: "general" },
    ],
    CH: [
      { number: "144", service: "medical" },
      { number: "112", service: "general" },
    ],
    AT: [
      { number: "144", service: "medical" },
      { number: "112", service: "general" },
    ],

    // 999 is the number people there know; 112 works alongside it.
    GB: [
      { number: "999", service: "general" },
      { number: "112", service: "general" },
    ],
    IE: [
      { number: "112", service: "general" },
      { number: "999", service: "general" },
    ],

    AU: [{ number: "000", service: "general" }],
    NZ: [{ number: "111", service: "general" }],
  };

/**
 * The emergency numbers for a country, or `null` when we do not know them.
 *
 * `null` is a real answer and the caller must render it as one — "call your
 * local emergency services", with no digits. See the note on never inventing a
 * number above.
 */
export function resolveEmergencyNumbers(
  countryCode: string | null | undefined,
): EmergencyContacts | null {
  const normalized = normalizeCountryCode(countryCode);
  if (!normalized) return null;
  const numbers = EMERGENCY_NUMBERS[normalized];
  if (!numbers || numbers.length === 0) return null;
  return { countryCode: normalized, numbers };
}

/**
 * Which record named a section's country: the IP address (web, where she is
 * right now as far as the network can tell), the device's region setting, or
 * her profile. Set only when two sections are listed; null when there is one.
 */
export type EmergencyBasis = "ip" | "device" | "profile";

export type EmergencySection = {
  contacts: EmergencyContacts;
  basis: EmergencyBasis | null;
};

/**
 * The sections the escalation card lists, in order: where she seems to be
 * first, then the profile country when it differs. "Here" is the same guess
 * sign-in makes — the IP address on web, the region setting on a phone — and
 * neither it nor the profile is certain to be where she *is*, so when they
 * disagree both are shown, each saying which record named it, and she picks
 * the one that applies. One section when they agree or only one is known;
 * none when neither country has listed numbers.
 */
export function resolveEmergencySections({
  hereCountry,
  hereBasis,
  profileCountry,
}: {
  hereCountry: string | null | undefined;
  hereBasis: "ip" | "device";
  profileCountry: string | null | undefined;
}): EmergencySection[] {
  const here = resolveEmergencyNumbers(hereCountry);
  const profile = resolveEmergencyNumbers(profileCountry);
  if (here && profile && here.countryCode !== profile.countryCode) {
    return [
      { contacts: here, basis: hereBasis },
      { contacts: profile, basis: "profile" },
    ];
  }
  const known = here ?? profile;
  return known ? [{ contacts: known, basis: null }] : [];
}

/**
 * A `tel:` target for a number as written.
 *
 * Keeps digits, a leading `+`, and the `#`/`*` a direct-dial extension can
 * carry; drops the spaces, dots and brackets a clinic writes its number with.
 * Returns `null` when nothing dialable is left, so a malformed record renders
 * as plain text rather than as a link that fails silently.
 */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const trimmed = phone.trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  const dialable = trimmed.replaceAll(/[^0-9#*]/g, "");
  if (!dialable) return null;
  return `tel:${plus}${dialable}`;
}
